import Button from '@berlitz/button'
import { H1, H3 } from '@berlitz/globals'
import { Tick } from '@berlitz/icons'
import Modal from '@berlitzplatforms/micro.ui.modal'
import TextArea from '@berlitz/text-area'
import { Field, FieldProps, Form, Formik, FormikHelpers as FormikActions, FormikErrors, FormikProps } from 'formik'
import * as React from 'react'
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'

import Spinner from '@berlitz/spinner'
import { CenterText, SpinnerWrapper } from '@components/Modal/style'
import {
  ActionButtons,
  Box,
  Counter,
  IconCircle,
  Success,
  TextAreaBox,
} from '@layouts/LessonRegistration/AddNoteModal/style'
import { AddNoteModalProps, FormProps, IAddNoteState } from './interface'

class AddNoteModal extends React.Component<AddNoteModalProps & InjectedIntlProps, IAddNoteState> {
  constructor(props) {
    super(props)
    this.state = {
      charLimit: 120,
      saved: false,
      deleted: false,
    }
  }

  handleValidation = (values: FormProps) => {
    const { charLimit } = this.state
    const errors: FormikErrors<FormProps> = {}

    if (values.note && values.note.length > charLimit) {
      // this error will not be shown on the page
      // but will be used as an indicator for `Formik` to create an error
      errors.note = 'The maximum number of characters exceeded'
    }

    return errors
  }

  handleSubmit = (values: FormProps, actions: FormikActions<FormProps>) => {
    if (typeof this.props.onSave === 'function') {
      this.props.onSave(values.note)
      actions.resetForm({ values })
      this.setState({ saved: true })
    }
  }

  render() {
    const { charLimit, saved, deleted } = this.state
    const { intl, open, onClose, initialValues, isProcessingSave, isLoading, isLoadingText, isNotEditable } = this.props
    return (
      <Modal
        open={open}
        onClose={() => {
          onClose()
          setTimeout(() => this.setState({ saved: false, deleted: false }), 500)
        }}
      >
        {isLoading ? (
          <>
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
            <CenterText>
              <H1 disableMargin>{isLoadingText}</H1>
            </CenterText>
          </>
        ) : (
          <Box>
            {saved && !isProcessingSave ? (
              <Success>
                <IconCircle color="error">
                  <Tick size="lg" />
                </IconCircle>
                <H1 disableMargin>
                  <FormattedMessage id={deleted ? 'Your note has been deleted' : 'Your note has been added'} />
                </H1>
              </Success>
            ) : (
              <>
                <H3 data-testid="note-text" disableMargin>
                  {initialValues.note ? <FormattedMessage id="Edit note" /> : <FormattedMessage id="Add note" />}
                </H3>
                <Formik
                  initialValues={initialValues}
                  onSubmit={this.handleSubmit}
                  validate={this.handleValidation}
                  render={({ dirty, errors, values, isValid, ...rest }: FormikProps<FormProps>) => (
                    <Form>
                      <Field
                        name="note"
                        render={({ field }: FieldProps) => {
                          const charLimitExceeds = values.note.length > charLimit

                          return (
                            <TextAreaBox error={charLimitExceeds}>
                              <TextArea
                                {...field}
                                placeholder={intl.formatMessage({
                                  id: initialValues.note ? 'Edit note' : 'Add note',
                                })}
                                disabled={isNotEditable}
                              />

                              <Counter error={charLimitExceeds}>{charLimit - values.note.length}</Counter>
                            </TextAreaBox>
                          )
                        }}
                      />

                      <ActionButtons>
                        <Button onClick={onClose}>
                          <span>
                            <FormattedMessage id="Cancel" />
                          </span>
                        </Button>
                        <Button type="submit" disabled={!isValid || !dirty}>
                          <FormattedMessage id="Save" />
                        </Button>
                      </ActionButtons>
                    </Form>
                  )}
                />
              </>
            )}
          </Box>
        )}
      </Modal>
    )
  }
}

export default injectIntl(AddNoteModal)
